import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { defaultAccommodationTypesOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Form } from 'antd';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

export type AccommodationFormValues = {
  value: string;
  baseBenefitOnType: Option<string> | null;
  description: string;
  startDate: string;
  endDate: string;
  valueOfFurnitureProvided: string;
  eeContribution: string;
  totalEeContribution: string;
};

export type AccommodationFormRef = {
  submit: () => void;
  setFieldsValue: (values: Partial<AccommodationFormValues>) => void;
};

type AccommodationFormProps = {
  formRef?: Ref<AccommodationFormRef>;
  onSubmit?: (values: AccommodationFormValues) => void;
  defaultValues?: AccommodationFormValues;
};

const AccommodationForm: React.FC<AccommodationFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<AccommodationFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    setFieldsValue(values: Partial<AccommodationFormValues>) {
      form.setFieldsValue(values);
    },
  }));

  const onFinish = (values: AccommodationFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={defaultValues}
    >
      <Form.Item
        label={messages['common.type']}
        name="baseBenefitOnType"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppSelect
          options={defaultAccommodationTypesOptions}
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.amount']}
        name="value"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.description']}
        name="description"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>
      <Form.Item
        label={messages['common.valueOfFurnitureProvided']}
        name="valueOfFurnitureProvided"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.startDate']}
        name="startDate"
        rules={[
          {
            required: true,
            message: messages['form.pleaseSelect'],
          },
        ]}
      >
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
        />
      </Form.Item>
      <Form.Item label={messages['common.endDate']} name="endDate">
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.employeeContribution']}
        name="eeContribution"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.totalEmployeeContribution']}
        name="totalEeContribution"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
          disabled
        />
      </Form.Item>
    </AppFormContainer>
  );
};

export default AccommodationForm;
